import { useRef, useEffect } from 'react';

const APPLIED_LISTENERS = new Set();
let AUTO_INCREMENTED_ID = 0;

/**
 * bind the given listener to clicks happening outside any of the reference
 * nodes (useful when binding automatic closing of toggled layers and similar).
 */
function useClickOutsideListener(listener, refs, {
  useCapturePhase = false,
  forceCallbackOnClick = false,
  disabled = false,
  ignore = []
} = {}) {
  const listenerId = useRef(++AUTO_INCREMENTED_ID).current;
  useEffect(() => {
    if (listener === undefined || disabled) {
      return;
    }
    APPLIED_LISTENERS.add(listenerId);
    const handleClick = evt => {
      const targetNode = evt.target;
      const refArr = Array.isArray(refs) ? refs : [refs];
      const ignoredElements = ignore.map(it => document.querySelector(it)).filter(it => !!it);
      if (targetNode === null) {
        return;
      }
      for (const toIgnore of ignoredElements) {
        if (toIgnore === targetNode || toIgnore.contains(targetNode)) {
          return;
        }
      }
      if (!forceCallbackOnClick && listenerId !== getLastAddedItem(APPLIED_LISTENERS)) {
        // only the last applied clickOutside listener may be triggered in a
        // single click; otherwise we could accidentally close multiple layers
        // of ui at once even if that's not what the user intended to do
        return;
      }

      // check if the clicked node is contained within one of the given refs, if
      // no matching parent node was given then bail out
      const containedWithinRef = refArr.find(ref => {
        return ref.current !== null && (ref.current === targetNode || ref.current.contains(targetNode));
      });
      if (containedWithinRef === undefined) {
        listener(evt);
      }
    };
    const applyListenerTimeout = window.setTimeout(() => window.addEventListener("click", handleClick, {
      capture: useCapturePhase
    }));
    return () => {
      window.clearTimeout(applyListenerTimeout);
      window.removeEventListener("click", handleClick, {
        capture: useCapturePhase
      });
      APPLIED_LISTENERS.delete(listenerId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listener, listenerId, refs, disabled]);
}
function getLastAddedItem(set) {
  const values = set.values();
  let prevValue;
  let currValue;
  do {
    prevValue = currValue;
    currValue = values.next();
  } while (currValue.done !== true);
  if (prevValue !== undefined) {
    return prevValue.value;
  }
}

export { useClickOutsideListener };
//# sourceMappingURL=useClickOutsideListener.js.map
